import { FC, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { Form } from 'antd';
import { UploadFile as UploadFileAntd, UploadProps } from 'antd/es/upload';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { FieldContainerIw } from '@/_pages/interwill/components/FieldContainerIw';
import { useFileEncryptionUpload } from '@/entities/file/hooks/use-file-encryption-upload';
import { SupportedDoc, useIwExecutionStore } from '@/entities/interwills';
import { useApi } from '@/shared/api';
import { Input } from '@/shared/components';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';
import { DatePicker } from '@/shared/uikit/DatePicker';
import { UploadFile } from '@/shared/uikitV2/UploadFile';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { SignedWillInfoModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../containers/ModalContainerV1';
import { InterwillStyledForm } from '../CreateEntityModal/styled';

type FormParams = {
  signingDate: string;
  signingPlace: string;
  storageLocation: string;
  signedWill?: UploadFileAntd<{ filePath?: string }>;
  supportingDocs: UploadFileAntd<{ filePath?: string }>[];
};

export const SignedWillInfoModal: FC<SignedWillInfoModalProps> = () => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'signedWillInfo' });

  const modals = useModalsStore();
  const {
    signedWill,
    signingPlace,
    storageLocation,
    signingDate,
    supportedDocuments,
    setSignedWill,
    setSupportedDocuments,
    setSigningDate,
    setSigningPlace,
    setStorageLocation,
    setDecodedSignedWill,
  } = useIwExecutionStore();

  const { keyPair } = useKeyPairStore();
  const { encryptAndUploadFile } = useFileEncryptionUpload();
  const api = useApi();

  const sjDatabase = useSJDatabase();
  const formRef = useRef<HTMLDivElement>(null);
  const {
    control,
    handleSubmit: formHandleSubmit,
    formState: { isSubmitting },
  } = useForm<FormParams>({
    defaultValues: {
      signedWill: signedWill
        ? {
            percent: 100,
            size: signedWill.size,
            url: signedWill.url,
          }
        : undefined,
      signingDate: signingDate ?? '',
      signingPlace: signingPlace ?? '',
      storageLocation: storageLocation ?? '',
      supportingDocs: supportedDocuments.map((item) => ({
        name: item.name,
        percent: 100,
        size: item.size,
        url: item.url,
      })),
    },
  });

  const removeFile = async (filePath: string) => {
    await api.filesDeleteFile(filePath);

    const fileId = `${filePath}:${keyPair?.publicKey}`;
    const fileKey = sjDatabase.collections.encryptedFileKey?.findById(fileId);
    if (!fileKey || !('signature' in fileKey)) return;

    sjDatabase.collections.encryptedFileKey?.removeById(fileId);

    const fileMeta = sjDatabase.collections.fileMetaInfos?.findOne(
      (item) => item.filePath === filePath,
    );

    if (fileMeta && 'signature' in fileMeta) {
      sjDatabase.collections.fileMetaInfos?.removeById(fileMeta.id);
    }
  };

  const onSubmit = async (values: FormParams) => {
    const isUploading = [...values.supportingDocs, values.signedWill].some(
      (item) => item?.status === 'uploading',
    );
    if (isUploading) return;

    const removedDocs = supportedDocuments.filter(
      (oldDoc) => !values.supportingDocs.some((newDoc) => newDoc.url === oldDoc.url),
    );

    await Promise.allSettled(removedDocs.map((doc) => removeFile(doc.url)));

    const newSupportedDocuments: SupportedDoc[] = values.supportingDocs
      .map((item) => {
        if (item.url && item.size && item.name) {
          return {
            name: item.name,
            size: item.size,
            url: item.url,
          };
        }
        if (item.originFileObj && item.response?.filePath) {
          return {
            name: item.originFileObj?.name,
            url: item.response?.filePath,
            size: item.originFileObj?.size,
          };
        }
        return null;
      })
      .filter((item) => item !== null);

    let deleteSigned = false;

    const filePath = values.signedWill?.response?.filePath;
    if (values.signedWill?.originFileObj && filePath) {
      deleteSigned = true;
      setSignedWill({
        name: 'Signed_Last_Will_and_Testament.pdf',
        size: values.signedWill.originFileObj.size,
        url: filePath,
      });
    }
    if (!values.signedWill) {
      deleteSigned = true;
      setSignedWill(null);
    }

    setDecodedSignedWill(values.signedWill?.originFileObj ?? null);

    if (deleteSigned && signedWill) {
      removeFile(signedWill.url);
    }

    setSupportedDocuments(newSupportedDocuments);
    setSigningDate(values.signingDate);
    setSigningPlace(values.signingPlace);
    setStorageLocation(values.storageLocation);

    modals.closeModal();
  };

  const handleUpload: UploadProps['customRequest'] = async ({
    file,
    onProgress,
    onSuccess,
    onError,
  }) => {
    if (!(file instanceof File)) {
      onError?.(new Error());
      return;
    }

    if (file.type !== 'application/pdf') {
      onError?.(new Error());
      return;
    }

    onProgress?.({ percent: 20 });

    const filePath = await encryptAndUploadFile(file);
    onSuccess?.({ filePath });
  };

  const handleRemove: UploadProps['onRemove'] = (file) => {
    if (file.response?.filePath) {
      removeFile(file.response?.filePath);
    }
  };

  return (
    <ModalContainerV1 title={t('title')}>
      <InterwillStyledForm onFinish={formHandleSubmit(onSubmit)}>
        <InnerForm ref={formRef}>
          <Controller
            name={'signedWill'}
            control={control}
            render={({ field }) => {
              return (
                <UploadFile
                  maxCount={1}
                  onChange={(data) => {
                    field.onChange(data.fileList[0] ?? null);
                  }}
                  fileList={field.value ? [field.value] : []}
                  hideDropArea={Boolean(field.value)}
                  accept="application/pdf"
                  title={t('uploadButton')}
                  description={t('uploadDescription')}
                  listType="picture"
                  forceFileName="Signed_Last_Will_and_Testament.pdf"
                  customRequest={handleUpload}
                  onRemove={handleRemove}
                />
              );
            }}
          />
          <Controller
            name={'signingDate'}
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <DatePicker
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      type={'text'}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />
          <Controller
            name={'signingPlace'}
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      type={'text'}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />
          <Controller
            name={'storageLocation'}
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      type={'text'}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />
          <Controller
            name={'supportingDocs'}
            control={control}
            render={({ field }) => {
              return (
                <UploadFile
                  multiple
                  accept="application/pdf"
                  title={t('uploadSupport')}
                  description={t('uploadSupportDescription')}
                  listType="picture"
                  fileList={field.value}
                  onChange={(data) => {
                    field.onChange(data.fileList);
                    if (field.value.length <= data.fileList.length) {
                      formRef.current?.scrollTo({
                        top: formRef.current.scrollHeight,
                        behavior: 'smooth',
                      });
                    }
                  }}
                  customRequest={handleUpload}
                  onRemove={handleRemove}
                />
              );
            }}
          />
        </InnerForm>
        <Buttons>
          <ButtonIw loading={isSubmitting} disabled={isSubmitting} htmlType="submit">
            {t('btn')}
          </ButtonIw>
        </Buttons>
      </InterwillStyledForm>
    </ModalContainerV1>
  );
};

const InnerForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow: auto;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 40px;
`;
